<template>
  <section v-if="visible" class="network-panel" aria-live="polite">
    <div class="network-panel-head">
      <span class="network-panel-icon" aria-hidden="true">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071a10 10 0 0114.142 0M4.93 9.172a14 14 0 0114.14 0" />
        </svg>
      </span>
      <div class="network-panel-head-text">
        <h4 class="network-panel-title">{{ $t('Package_Network_Title') }}</h4>
        <p class="network-panel-subtitle">{{ subtitleText }}</p>
      </div>
    </div>

    <div v-if="loading" class="network-panel-body">
      <div v-for="n in 2" :key="n" class="network-skeleton-row">
        <div class="network-skeleton-flag"></div>
        <div class="network-skeleton-lines">
          <div class="network-skeleton-line network-skeleton-line--short"></div>
          <div class="network-skeleton-line"></div>
        </div>
      </div>
    </div>

    <div v-else-if="isFallback" class="network-panel-body network-panel-fallback">
      <p class="network-fallback-text">{{ fallbackText }}</p>
    </div>

    <div v-else class="network-panel-body">
      <div
        v-for="(location, index) in locations"
        :key="`${location.locationCode || location.locationName}-${index}`"
        class="network-location-row"
      >
        <div class="network-location-main">
          <span class="network-location-flag-box">
            <span
              :class="`flag-icon flag-icon-${flagCode(location).toLowerCase()}`"
              class="network-location-flag"
              :aria-label="locationLabel(location)"
            ></span>
          </span>
          <span class="network-location-name">{{ locationLabel(location) }}</span>
        </div>
        <ul class="network-operator-list">
          <li
            v-for="(operator, opIndex) in location.operators || []"
            :key="`${operator.operatorName}-${opIndex}`"
            class="network-operator-item"
          >
            <span class="network-operator-name">{{ operator.operatorName }}</span>
            <span v-if="operator.networkType" class="network-operator-badge">{{ operator.networkType }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getCountryRegionDisplayName, getFlagCountryCode } from '@/utils/countryRegionDisplay'
import logger from '@/utils/logger'

const props = defineProps({
  productId: {
    type: [Number, String],
    required: true,
  },
})

const api = inject('api')
const { t, te, locale } = useI18n()

const loading = ref(false)
const disclosure = ref(null)
const cache = new Map()

const locations = computed(() => disclosure.value?.locations || [])

const hasContent = computed(() => {
  if (!disclosure.value) {
    return false
  }
  if (disclosure.value.disclosureType === 'fallback') {
    return Boolean(disclosure.value.fallbackText)
  }
  return locations.value.length > 0
})

const visible = computed(() => loading.value || hasContent.value)
const fallbackText = computed(() => disclosure.value?.fallbackText || '')
const isFallback = computed(() => disclosure.value?.disclosureType === 'fallback')

const subtitleText = computed(() => {
  if (loading.value) {
    return t('Package_Network_Loading')
  }
  if (isFallback.value) {
    return t('Package_Network_Subtitle_Fallback')
  }
  const count = locations.value.length
  return t('Package_Network_Subtitle', count, { count })
})

const cacheKey = () => `${props.productId}:${locale.value}`

const locationLabel = (location) => {
  if (location?.locationName) {
    return location.locationName
  }
  return getCountryRegionDisplayName({
    code: location?.locationCode,
    locale: locale.value,
    t,
    te,
  })
}

const flagCode = (location) => {
  return getFlagCountryCode(location?.locationCode || location?.locationName || 'UN')
}

const loadDisclosure = async () => {
  const productId = Number(props.productId)
  if (!productId || !api?.product?.getNetworkOperators) {
    disclosure.value = null
    return
  }

  const key = cacheKey()
  if (cache.has(key)) {
    disclosure.value = cache.get(key)
    return
  }

  loading.value = true
  disclosure.value = null
  try {
    const res = await api.product.getNetworkOperators(productId)
    if (res?.data?.status === '0' && res?.data?.data) {
      cache.set(key, res.data.data)
      disclosure.value = res.data.data
    }
  } catch (error) {
    logger.warn('Failed to load network operators:', error)
    disclosure.value = null
  } finally {
    loading.value = false
  }
}

watch(
  () => [props.productId, locale.value],
  () => {
    loadDisclosure()
  },
  { immediate: true }
)
</script>

<style scoped>
.network-panel {
  border: 1px solid rgb(243 244 246);
  border-radius: 1rem;
  background: linear-gradient(180deg, rgb(250 250 255) 0%, rgb(255 255 255) 100%);
  overflow: hidden;
}

:root.dark .network-panel {
  border-color: rgb(55 65 81);
  background: linear-gradient(180deg, rgb(31 41 55) 0%, rgb(17 24 39) 100%);
}

.network-panel-head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1rem 0.75rem;
}

.network-panel-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.75rem;
  background: rgba(124, 58, 237, 0.1);
  color: rgb(124 58 237);
  flex-shrink: 0;
}

.network-panel-head-text {
  min-width: 0;
}

.network-panel-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(17 24 39);
  line-height: 1.3;
}

:root.dark .network-panel-title {
  color: rgb(243 244 246);
}

.network-panel-subtitle {
  margin-top: 0.15rem;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgb(107 114 128);
}

:root.dark .network-panel-subtitle {
  color: rgb(156 163 175);
}

.network-panel-body {
  padding: 0 0.75rem 0.75rem;
  max-height: 16.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.network-panel-fallback {
  padding-top: 0.25rem;
}

.network-fallback-text {
  padding: 0.75rem 0.85rem;
  border-radius: 0.85rem;
  background: rgb(249 250 251);
  font-size: 0.8125rem;
  line-height: 1.55;
  color: rgb(75 85 99);
}

:root.dark .network-fallback-text {
  background: rgb(31 41 55);
  color: rgb(209 213 219);
}

.network-location-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.55rem;
  border-top: 1px solid rgb(243 244 246);
}

.network-location-row:first-child {
  border-top: none;
}

:root.dark .network-location-row {
  border-top-color: rgb(55 65 81);
}

.network-location-main {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  flex: 1 1 42%;
}

.network-location-flag-box {
  width: 1.65rem;
  height: 1.25rem;
  border-radius: 0.35rem;
  overflow: hidden;
  border: 1px solid rgb(229 231 235);
  background: rgb(249 250 251);
  flex-shrink: 0;
}

:root.dark .network-location-flag-box {
  border-color: rgb(75 85 99);
  background: rgb(31 41 55);
}

.network-location-flag {
  display: block;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.network-location-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgb(31 41 55);
  line-height: 1.35;
}

:root.dark .network-location-name {
  color: rgb(243 244 246);
}

.network-operator-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1 1 58%;
  align-items: flex-end;
}

.network-operator-item {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  max-width: 100%;
}

.network-operator-name {
  font-size: 0.78rem;
  line-height: 1.35;
  color: rgb(55 65 81);
  text-align: right;
}

:root.dark .network-operator-name {
  color: rgb(209 213 219);
}

.network-operator-badge {
  flex-shrink: 0;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: rgb(243 244 246);
  color: rgb(75 85 99);
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
}

:root.dark .network-operator-badge {
  background: rgb(55 65 81);
  color: rgb(209 213 219);
}

.network-skeleton-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.55rem;
}

.network-skeleton-flag,
.network-skeleton-line {
  border-radius: 0.5rem;
  background: linear-gradient(90deg, rgb(243 244 246) 25%, rgb(229 231 235) 50%, rgb(243 244 246) 75%);
  background-size: 200% 100%;
  animation: network-shimmer 1.2s ease-in-out infinite;
}

:root.dark .network-skeleton-flag,
:root.dark .network-skeleton-line {
  background: linear-gradient(90deg, rgb(55 65 81) 25%, rgb(75 85 99) 50%, rgb(55 65 81) 75%);
  background-size: 200% 100%;
}

.network-skeleton-flag {
  width: 1.65rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.network-skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.network-skeleton-line {
  height: 0.7rem;
  width: 100%;
}

.network-skeleton-line--short {
  width: 42%;
}

@keyframes network-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 360px) {
  .network-location-row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .network-operator-list {
    align-items: flex-start;
    width: 100%;
    padding-left: 2.2rem;
  }

  .network-operator-item {
    justify-content: flex-start;
  }

  .network-operator-name {
    text-align: left;
  }
}
</style>
